import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BarChart2, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTOR_CONFIG = {
  healthcare:         { label: 'Healthcare',        color: '#008C86' },
  government:         { label: 'Government',         color: '#D9A245' },
  'business-finance': { label: 'Business & Finance', color: '#081D30' },
  education:          { label: 'Education',          color: '#7B6E9C' },
  'technology-ai':    { label: 'Technology & AI',    color: '#1B6B72' },
  'workforce-labor':  { label: 'Workforce & Labor',  color: '#B45309' },
};

export const MostEngagedWidget = () => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], page: 1, total_pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchPage = useCallback(async (p) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/homepage/most-engaged?page=${p}`);
      const json = await res.json();
      setData(json);
    } catch {
      setData({ items: [], page: 1, total_pages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  const goTo = (p) => {
    if (p < 1 || p > data.total_pages) return;
    setPage(p);
  };

  return (
    <section className="op-sb-widget" data-testid="most-engaged-widget">
      {/* Header */}
      <div className="op-sb-head">
        <div className="op-sb-head-left">
          <h2 className="op-sb-title">Most Engaged Sector Briefs</h2>
          <p className="op-sb-subtext">What readers are engaging with now.</p>
        </div>
        <span className="op-sb-sort-label">Sort: Most Engaged →</span>
      </div>

      {/* List */}
      <div className="op-sb-list" data-testid="most-engaged-list">
        {loading ? (
          <SkeletonRows count={8} />
        ) : data.items.length === 0 ? (
          <div className="op-sb-empty" data-testid="most-engaged-empty">
            No articles published in the last 5 days yet.<br />
            <small style={{ color: '#999', marginTop: 6, display: 'block' }}>
              Engagement data field: <code>display_view_count</code> — all values currently 0.
            </small>
          </div>
        ) : (
          data.items.map((item, idx) => {
            const rank = (page - 1) * 8 + idx + 1;
            const sector = SECTOR_CONFIG[item.primary_pillar] || { label: item.primary_pillar || 'General', color: '#6E716D' };
            const slug = item.slug ? `/article/${item.slug}` : '#';
            return (
              <Link
                key={item.id || idx}
                to={slug}
                className="op-sb-item"
                data-testid={`me-item-${rank}`}
              >
                {/* Thumbnail flush left */}
                {item.featured_image_url ? (
                  <img
                    src={item.featured_image_url}
                    alt=""
                    className="op-sb-thumb"
                    loading="lazy"
                  />
                ) : (
                  <div className="op-sb-thumb-placeholder">
                    <BarChart2 size={18} strokeWidth={1.5} style={{ color: '#D8D0C0' }} />
                  </div>
                )}

                {/* Rank */}
                <span className="op-sb-rank">{String(rank).padStart(2, '0')}</span>

                {/* Sector + Title */}
                <div className="op-sb-meta">
                  <div className="op-sb-sector" style={{ color: sector.color }}>
                    {sector.label}
                  </div>
                  <div className="op-sb-article-title">{item.title}</div>
                </div>

                {/* Engagement indicator */}
                <div className="op-sb-right">
                  <BarChart2
                    size={16}
                    strokeWidth={1.8}
                    className="op-sb-engage-icon"
                    aria-label="Engagement indicator"
                  />
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {!loading && data.total > 0 && (
        <div className="op-sb-pagination" data-testid="me-pagination">
          <button
            className="op-sb-page-btn"
            onClick={() => goTo(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            data-testid="me-prev-btn"
          >
            <ChevronLeft size={14} strokeWidth={2} />
          </button>
          <span className="op-sb-page-label" data-testid="me-page-label">
            Page {data.page} of {data.total_pages}
          </span>
          <button
            className="op-sb-page-btn"
            onClick={() => goTo(page + 1)}
            disabled={page >= data.total_pages}
            aria-label="Next page"
            data-testid="me-next-btn"
          >
            <ChevronRight size={14} strokeWidth={2} />
          </button>
        </div>
      )}
    </section>
  );
};

const SkeletonRows = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="op-sb-item op-sb-item--skeleton">
        <div className="op-sb-thumb-placeholder" style={{ background: '#e8e4dc' }} />
        <span className="op-sb-rank" style={{ color: 'transparent', background: '#e8e4dc', borderRadius: 3 }}>00</span>
        <div className="op-sb-meta">
          <div style={{ height: 9, width: '38%', background: '#e8e4dc', borderRadius: 3 }} />
          <div style={{ height: 12, width: '78%', background: '#e8e4dc', borderRadius: 3 }} />
          <div style={{ height: 12, width: '55%', background: '#e8e4dc', borderRadius: 3 }} />
        </div>
        <div className="op-sb-right">
          <div style={{ width: 18, height: 18, background: '#e8e4dc', borderRadius: 3 }} />
        </div>
      </div>
    ))}
  </>
);
